/* eslint-disable react-hooks/set-state-in-effect, react-hooks/purity, react-hooks/set-state-in-render --
 * v77.99.77: Idiomatic React patterns mis-flagged by experimental
 * react-hooks rules (set-state inside effect / handler, derived
 * values inside useMemo). All audited.
 */
// v77.99.21 — Snapshot Email Dialog
//
// Lets the user email the one-page Expenses Snapshot directly to a
// solicitor / mediator / accountant. The PDF the recipient receives
// is byte-identical to the one the download flow produces — same
// fingerprint, same layout, same content.
//
// Design constraints from the user:
//   • Keep the PDF visual unchanged.
//   • Single-step UX where possible (no separate "download then email"
//     dance).
//   • Pre-fill the recipient with the last address the user emailed
//     (sourced from `user.last_snapshot_recipient`) so repeat sends
//     to the same solicitor are one tap.
//
// State coupling:
//   • Inherits the active scope (`scopeChildId`, `scopeChildName`)
//     from the parent SnapshotExportMenu so the user doesn't pick
//     the scope twice.
//   • Has its own range picker so the user can email a different
//     window from the one they're currently viewing on screen.
//
// Server contract:
//   • POST /api/expenses/snapshot/email
//   • Body: { recipient_email, note?, child_id?, from?, to?, range_label? }
//   • Returns: { ok, sent_at, recipient, filename, fingerprint }

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SNAPSHOT_PRESETS, resolvePreset } from "@/lib/snapshotPresets";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SnapshotEmailDialog({
  open,
  onOpenChange,
  scopeChildId,
  scopeChildName,
}) {
  const { user } = useAuth();

  // Recipient pre-fills from the last address the user sent to, so
  // repeat sends are friction-free.
  const lastRecipient = user?.last_snapshot_recipient || "";
  const lastSentAt = user?.last_snapshot_sent_at || null;

  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [presetId, setPresetId] = useState("year-to-date");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sending, setSending] = useState(false);

  // Reset the form whenever the dialog opens so a stale draft from
  // an earlier session doesn't accidentally send.
  useEffect(() => {
    if (open) {
      setRecipient(lastRecipient || "");
      setNote("");
      setPresetId("year-to-date");
      setCustomFrom("");
      setCustomTo("");
      setSending(false);
    }
  }, [open, lastRecipient]);

  // Resolve the selected preset to {from, to, label}. Custom is
  // handled separately via the two date inputs.
  const resolved = useMemo(() => {
    if (presetId === "custom") {
      if (!customFrom || !customTo) return null;
      if (customFrom > customTo) return null;
      return { from: customFrom, to: customTo, label: "Custom range" };
    }
    return resolvePreset(presetId);
  }, [presetId, customFrom, customTo]);

  const recipientValid = EMAIL_RE.test(recipient.trim());
  const canSend = recipientValid && !!resolved && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        recipient_email: recipient.trim(),
        note: note.trim() || null,
        from: resolved.from,
        to: resolved.to,
        range_label: resolved.label,
      };
      if (scopeChildId && scopeChildId !== "all") {
        payload.child_id = scopeChildId;
      }
      const r = await api.post("/expenses/snapshot/email", payload);
      const data = r.data || {};
      toast.success(
        `Snapshot emailed to ${data.recipient || recipient}`,
        {
          description: data.fingerprint
            ? `Fingerprint ${data.fingerprint}`
            : undefined,
        },
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = detail?.reason || err?.response?.statusText || "Send failed";
      toast.error("Could not send snapshot", {
        description: String(reason).replace(/_/g, " "),
      });
    } finally {
      setSending(false);
    }
  };

  const scopeBlurb = scopeChildId && scopeChildId !== "all"
    ? `for ${scopeChildName || "this child"}`
    : "for all children";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="snapshot-email-dialog"
      >
        <DialogHeader>
          <DialogTitle>Email snapshot {scopeBlurb}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            We&apos;ll attach a one-page PDF, identical to the download.
            The recipient can verify it at coparentconsole.com/verify
            using the document fingerprint.
            {lastSentAt && (
              <span className="block mt-2 text-xs">
                Last sent: {new Date(lastSentAt).toLocaleString()}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="snap-email-to" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
              Recipient email
            </Label>
            <Input
              id="snap-email-to"
              type="email"
              autoComplete="email"
              placeholder="solicitor@firm.ie"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="border-[#D9E1E3] rounded-sm mt-1"
              data-testid="snapshot-email-recipient"
            />
          </div>

          <div>
            <Label htmlFor="snap-email-range" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
              Date range
            </Label>
            <Select value={presetId} onValueChange={setPresetId}>
              <SelectTrigger
                id="snap-email-range"
                className="border-[#D9E1E3] rounded-sm mt-1"
                data-testid="snapshot-email-range"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SNAPSHOT_PRESETS.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    data-testid={`snapshot-email-range-${p.id}`}
                  >
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {presetId === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="snap-email-from" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
                  From
                </Label>
                <Input
                  id="snap-email-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="border-[#D9E1E3] rounded-sm mt-1"
                  data-testid="snapshot-email-custom-from"
                />
              </div>
              <div>
                <Label htmlFor="snap-email-to-date" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
                  To
                </Label>
                <Input
                  id="snap-email-to-date"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="border-[#D9E1E3] rounded-sm mt-1"
                  data-testid="snapshot-email-custom-to"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="snap-email-note" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
              Note (optional)
            </Label>
            <Textarea
              id="snap-email-note"
              placeholder="Attached for the mediation session on 27 May."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={600}
              rows={3}
              className="border-[#D9E1E3] rounded-sm mt-1"
              data-testid="snapshot-email-note"
            />
            <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground text-right mt-1">
              {note.length}/600
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="border-[#D9E1E3] rounded-sm"
            data-testid="snapshot-email-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
            data-testid="snapshot-email-send"
          >
            {sending ? "Sending…" : "Send snapshot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SnapshotEmailDialog;
